import { RouteProp, useRoute } from "@react-navigation/native";
import { useMutation } from "@tanstack/react-query";
import Button from "components/Button";
import HiddenView from "components/HiddenView";
import MainContainer from "components/MainContainer";
import Spacer from "components/Spacer";
import {
  fetchPlanPackagesForUpgrade,
  setStep,
} from "features/slices/buyFlowSlice";
import { PlanPackageDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useUpgradablePlansForSubscription from "hooks/useUpgradablePlansForSubscription";
import { AccountStackParamList } from "navigation/types";
import { getPlanChangeChargeFetcher } from "queries/account.queries";
import React, { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { toManageMemberSubscription } from "utils/navigationUtils";
import UpgradePlanStepOne from "./components/UpgradePlanStepOne";
import UpgradePlanStepTwo from "./components/UpgradePlanStepTwo";
import getPlanChanges from "./getPlanChanges";
import { PlanChanges } from "./types";

const UpgradePlans: React.FC = () => {
  const dispatch = useAppDispatch();
  const { upgradablePlans } = useAppSelector((state) => state.buyFlow);
  const { step } = useAppSelector((state) => state.buyFlow);
  const { account } = useAccount();
  const route = useRoute<RouteProp<AccountStackParamList, "UpgradePlans">>();
  const { subscriptionId } = route.params;
  const subscriptionIdNum = useMemo(
    () => Number(subscriptionId),
    [subscriptionId]
  );

  const goBack = () => {
    toManageMemberSubscription(String(selectedSubscription?.member.id));
  };

  const selectedSubscription = useMemo(() => {
    if (!account) return undefined;
    return account.subscriptions.find((sub) => sub.id === subscriptionIdNum);
  }, [subscriptionIdNum]);

  const upgradablePlansForSelectedSubscription =
    useUpgradablePlansForSubscription(selectedSubscription);

  // the plan that the user wants to upgrade to
  const [selectedUpgradePlan, setSelectedUpgradePlan] =
    useState<PlanPackageDetails | null>(null);
  const [planChanges, setPlanChanges] = useState<PlanChanges>([]);
  const { data: planChangeCharge, mutateAsync: getPlanChangeCharge } =
    useMutation(getPlanChangeChargeFetcher);

  // watch selectedUpgradePlan changes and fetch signup charge
  useEffect(() => {
    if (!selectedUpgradePlan || !selectedSubscription) return;
    const fetchPlanChangeCharge = async () => {
      const changes = getPlanChanges(
        account!.subscriptions,
        selectedUpgradePlan,
        selectedSubscription.planPackage!
      );
      if (changes.length === 0) {
        return;
      }
      await getPlanChangeCharge({
        accountId: account!.id,
        changes,
        source: "FANRALLY_APP",
      });
      setPlanChanges(changes);
    };
    fetchPlanChangeCharge();
  }, [selectedUpgradePlan, selectedSubscription]);

  useEffect(() => {
    if (
      upgradablePlansForSelectedSubscription &&
      upgradablePlansForSelectedSubscription.length > 0
    ) {
      setSelectedUpgradePlan(upgradablePlansForSelectedSubscription[0]);
    } else {
      setSelectedUpgradePlan(null);
    }
  }, [upgradablePlansForSelectedSubscription]);

  // fetch available plan packages offered by tenant when account is ready and list is empty
  useEffect(() => {
    if (!account) return;
    if (!upgradablePlans || upgradablePlans.length === 0) {
      dispatch(fetchPlanPackagesForUpgrade(account));
    }
  }, [account, upgradablePlans, dispatch]);

  return (
    <MainContainer goBackAction={goBack}>
      <View className="p-4">
        <HiddenView shouldHide={step !== 1}>
          <UpgradePlanStepOne
            title="Choose a plan to upgrade"
            planPackages={upgradablePlansForSelectedSubscription}
            setSelectedUpgradePlan={setSelectedUpgradePlan}
            selectedUpgradePlan={selectedUpgradePlan}
            planChangeCharge={planChangeCharge}
          />
        </HiddenView>
        <HiddenView shouldHide={step !== 2}>
          {selectedUpgradePlan !== null &&
            selectedSubscription !== undefined && (
              <UpgradePlanStepTwo
                planChangeCharge={planChangeCharge}
                selectedUpgradePlan={selectedUpgradePlan}
                selectedSubscription={selectedSubscription}
                planChanges={planChanges}
              />
            )}
        </HiddenView>
        <Spacer />
        <Button
          onPress={() => {
            if (step === 1) {
              goBack();
            } else {
              dispatch(setStep(step - 1));
            }
          }}
        >
          Back
        </Button>
      </View>
    </MainContainer>
  );
};

export default UpgradePlans;
